import { useMemo } from "react";

// Cyan-family palette that plays well with the Steele Cloud dark theme
const PALETTE = [
  "#22d3ee", // cyan-400
  "#a78bfa", // violet-400
  "#f472b6", // pink-400
  "#facc15", // yellow-400
  "#4ade80", // green-400
  "#fb923c", // orange-400
  "#38bdf8", // sky-400
  "#818cf8", // indigo-400
];

function fmtMoney(n) {
  return `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * SVG doughnut chart + legend. Purely presentational.
 *
 * Props:
 *  - products: [{ label, commission, ex_gst, line_count, share_pct, partners? }]
 *  - total: number (grand total commission)
 *  - title: string
 *  - subtitle: string
 *  - showPartnerBreakdown: bool  → render top-partner-per-product under each row
 *  - emptyLabel: string
 */
export default function ProductBreakdown({ products, total, title, subtitle, showPartnerBreakdown = false, emptyLabel = "No earnings yet.", testId = "product-breakdown" }) {
  const segments = useMemo(() => {
    const t = total || 0;
    if (t <= 0) return [];
    let cursor = 0;
    return products.map((p, i) => {
      const start = cursor;
      const value = p.commission || 0;
      const frac = value / t;
      cursor += frac;
      return {
        ...p,
        color: PALETTE[i % PALETTE.length],
        startFrac: start,
        endFrac: cursor,
      };
    });
  }, [products, total]);

  const size = 160;
  const stroke = 22;
  const radius = (size - stroke) / 2;
  const circ = 2 * Math.PI * radius;

  return (
    <div
      data-testid={testId}
      className="rounded-xl border border-slate-800/80 bg-slate-900 p-6 md:p-8"
    >
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-cyan-400 font-semibold">{title}</div>
        {subtitle && <div className="text-sm text-slate-500 mt-1">{subtitle}</div>}
      </div>

      {segments.length === 0 ? (
        <div className="py-10 text-center text-sm text-slate-500">{emptyLabel}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 items-start">
          {/* Doughnut */}
          <div className="relative shrink-0 mx-auto md:mx-0">
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
              <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#1e293b" strokeWidth={stroke} />
              {segments.map((s) => {
                const dash = (s.endFrac - s.startFrac) * circ;
                const gap = circ - dash;
                const offset = -s.startFrac * circ;
                return (
                  <circle
                    key={s.label}
                    cx={size / 2} cy={size / 2} r={radius}
                    fill="none"
                    stroke={s.color}
                    strokeWidth={stroke}
                    strokeDasharray={`${dash} ${gap}`}
                    strokeDashoffset={offset}
                    strokeLinecap="butt"
                  />
                );
              })}
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Total</div>
              <div className="font-mono text-lg font-medium text-slate-50">{fmtMoney(total)}</div>
            </div>
          </div>

          {/* Legend */}
          <ul className="space-y-3 min-w-0">
            {segments.map((s, i) => (
              <li key={s.label} data-testid={`product-legend-${i}`} className="flex items-start gap-3">
                <span className="mt-1 shrink-0 h-3 w-3 rounded-sm" style={{ backgroundColor: s.color }} />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 justify-between">
                    <div className="font-medium text-slate-100 truncate">{s.label}</div>
                    <div className="flex items-baseline gap-2 shrink-0">
                      <span className="font-mono text-sm text-cyan-400 font-medium">{fmtMoney(s.commission)}</span>
                      <span className="text-xs text-slate-500 font-mono">{s.share_pct?.toFixed(1)}%</span>
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {s.line_count} line item{s.line_count === 1 ? "" : "s"} · {fmtMoney(s.ex_gst)} ex-GST billed
                  </div>
                  {showPartnerBreakdown && s.partners && s.partners.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {s.partners.slice(0, 5).map((p) => (
                        <span
                          key={p.partner_code}
                          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-950 border border-slate-800 text-xs text-slate-300"
                        >
                          <span className="font-medium">{p.partner_name}</span>
                          <span className="font-mono text-cyan-400">{fmtMoney(p.commission)}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
